import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Switch,
    Button,
    Space,
    Typography,
    message,
    Spin,
    Tag,
    Row,
    Col,
    Divider,
    Tooltip,
    Modal,
    Select,
    Input,
    Form
} from 'antd';
import {
    SaveOutlined,
    ReloadOutlined,
    CopyOutlined,
    ExclamationCircleOutlined,
    SettingOutlined,
    SecurityScanOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchPermissionMatrix,
    bulkUpdateRolePermissions,
    autoSeedPermissions,
    copyRolePermissions
} from '../actions';
import { setError } from '../reducers';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const PermissionMatrix = () => {
    const dispatch = useDispatch();
    const {
        permissionMatrix,
        roles = [],
        loading,
        error
    } = useSelector(state => state.adminSettings);

    // Get roles from permissionMatrix if available, otherwise from roles state
    const availableRoles = permissionMatrix?.roles || roles;

    const [localError, setLocalError] = useState(null);

    console.log('PermissionMatrix Debug:', {
        permissionMatrix,
        roles,
        availableRoles,
        loading,
        error,
        localError
    });

    console.log('PermissionMatrix Debug:', {
        permissionMatrix,
        roles,
        availableRoles,
        loading,
        error
    });

    const [changes, setChanges] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [copyModalVisible, setCopyModalVisible] = useState(false);
    const [copyForm] = Form.useForm();

    useEffect(() => {
        // Clear any existing error when component mounts
        dispatch(setError(null));
        setLocalError(null);

        const fetchData = async () => {
            try {
                const result = await dispatch(fetchPermissionMatrix());
                if (!result.success) {
                    setLocalError(result.error);
                }
            } catch (err) {
                setLocalError(err.message || 'Lỗi khi tải ma trận phân quyền');
            }
        };

        fetchData();
    }, [dispatch]);

    const handlePermissionToggle = (roleId, permissionId, checked) => {
        const key = `${roleId}-${permissionId}`;
        const newChanges = {
            ...changes,
            [key]: checked
        };
        setChanges(newChanges);
        setHasChanges(Object.keys(newChanges).length > 0);
    };

    const handleSaveChanges = async () => {
        if (!hasChanges) {
            message.info('Không có thay đổi nào để lưu');
            return;
        }

        setSaving(true);
        try {
            const updates = Object.entries(changes).map(([key, hasPermission]) => {
                const [roleId, permissionId] = key.split('-');
                return {
                    roleId: parseInt(roleId),
                    permissionId: parseInt(permissionId),
                    hasPermission
                };
            });

            await dispatch(bulkUpdateRolePermissions(updates));
            
            // Clear local changes first
            setChanges({});
            setHasChanges(false);
            
            // Fetch fresh data from server to update UI with latest state
            await dispatch(fetchPermissionMatrix());
            
            message.success('Cập nhật quyền thành công!');
        } catch (error) {
            message.error('Lỗi khi cập nhật quyền: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleResetChanges = () => {
        if (!hasChanges) return;

        confirm({
            title: 'Xác nhận reset',
            icon: <ExclamationCircleOutlined />,
            content: 'Bạn có chắc chắn muốn hủy bỏ tất cả thay đổi?',
            onOk() {
                setChanges({});
                setHasChanges(false);
                message.info('Đã hủy bỏ thay đổi');
            },
        });
    };

    const handleSeedPermissions = async () => {
        confirm({
            title: 'Khởi tạo dữ liệu quyền',
            icon: <ExclamationCircleOutlined />,
            content: 'Điều này sẽ tạo dữ liệu mặc định cho roles và permissions. Tiếp tục?',
            onOk: async () => {
                setSeeding(true);
                try {
                    await dispatch(autoSeedPermissions());
                    message.success('Khởi tạo dữ liệu thành công!');
                    dispatch(fetchPermissionMatrix());
                } catch (error) {
                    message.error('Lỗi khi khởi tạo dữ liệu: ' + error.message);
                } finally {
                    setSeeding(false);
                }
            },
        });
    };

    const handleCopyPermissions = async (values) => {
        try {
            await dispatch(copyRolePermissions(values.fromRoleId, values.toRoleId));
            message.success('Sao chép quyền thành công!');
            setCopyModalVisible(false);
            copyForm.resetFields();
            dispatch(fetchPermissionMatrix());
        } catch (error) {
            message.error('Lỗi khi sao chép quyền: ' + error.message);
        }
    };

    const isPermissionEnabled = (roleId, permissionId) => {
        const key = `${roleId}-${permissionId}`;
        if (key in changes) {
            return changes[key];
        }

        // Check if role has this permission in its permissionIds array
        const role = availableRoles.find(r => r.id === roleId);
        if (role && role.permissionIds) {
            return role.permissionIds.includes(permissionId);
        }
        return false;
    };

    const getPermissionsByResource = () => {
        if (!permissionMatrix || !permissionMatrix.allPermissions) return {};

        return permissionMatrix.allPermissions.reduce((acc, permission) => {
            const resource = permission.resource || 'other';
            if (!acc[resource]) {
                acc[resource] = [];
            }
            acc[resource].push(permission);
            return acc;
        }, {});
    };

    const permissionsByResource = getPermissionsByResource();
    const resources = Object.keys(permissionsByResource);

    const columns = [
        {
            title: 'Quyền',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            width: 250,
            render: (text, record) => (
                <div>
                    <Text strong>{record.name}</Text>
                    {record.description && (
                        <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {record.description}
                            </Text>
                        </div>
                    )}
                </div>
            ),
        },
        ...availableRoles.map(role => ({
            title: (
                <div style={{ textAlign: 'center' }}>
                    <div>{role.name}</div>
                    <Tag color={role.isActive ? 'green' : 'red'} size="small">
                        {role.isActive ? 'Active' : 'Inactive'}
                    </Tag>
                </div>
            ),
            dataIndex: role.id,
            key: role.id,
            width: 120,
            align: 'center',
            render: (_, permission) => (
                <Switch
                    checked={isPermissionEnabled(role.id, permission.id)}
                    onChange={(checked) => handlePermissionToggle(role.id, permission.id, checked)}
                    size="small"
                    disabled={!role.isActive}
                />
            ),
        })),
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin/>
                <div style={{ marginTop: 16 }}>
                    <Text>Đang tải ma trận quyền...</Text>
                </div>
            </div>
        );
    }

    if (localError) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text type="danger">Lỗi: {localError}</Text>
                    <div style={{ marginTop: 16 }}>
                        <Button onClick={() => {
                            setLocalError(null);
                            dispatch(fetchPermissionMatrix());
                        }}>
                            Thử lại
                        </Button>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <div>
            <Row justify='end' align="middle" style={{ marginBottom: 10, marginTop: 10 }}>
                <Col>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => dispatch(fetchPermissionMatrix())}
                            loading={loading}
                        >
                            Làm mới
                        </Button>
                        <Button
                            icon={<SettingOutlined />}
                            onClick={handleSeedPermissions}
                            loading={seeding}
                            type="dashed"
                        >
                            Khởi tạo dữ liệu
                        </Button>
                        <Button
                            icon={<CopyOutlined />}
                            onClick={() => setCopyModalVisible(true)}
                            disabled={availableRoles.length < 2}
                        >
                            Sao chép quyền
                        </Button>
                        {hasChanges && (
                            <>
                                <Button onClick={handleResetChanges}>
                                    Hủy bỏ
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    onClick={handleSaveChanges}
                                    loading={saving}
                                >
                                    Lưu thay đổi
                                </Button>
                            </>
                        )}
                    </Space>
                </Col>
            </Row>

            {hasChanges && (
                <div style={{ marginBottom: 16 }}>
                    <Tag color="orange">
                        Có {Object.keys(changes).length} thay đổi chưa được lưu
                    </Tag>
                </div>
            )}

            {resources.length > 0 ? (
                resources.map(resource => (
                    <div key={resource} style={{ marginBottom: 24 }}>
                        <Title level={5} style={{ textTransform: 'capitalize', marginBottom: 16 }}>
                            <Tag color="blue">{resource.toUpperCase()}</Tag>
                        </Title>
                        <Table
                            dataSource={permissionsByResource[resource]}
                            columns={columns}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            bordered
                            scroll={{ x: 'max-content' }}
                            style={{ marginBottom: 16 }}
                        />
                    </div>
                ))
            ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text type="secondary">
                        Không có dữ liệu quyền. Hãy nhấn "Khởi tạo dữ liệu" để tạo dữ liệu mặc định.
                    </Text>
                </div>
            )}

            {/* Copy Permissions Modal */}
            <Modal
                title="Sao chép quyền giữa các vai trò"
                open={copyModalVisible}
                onCancel={() => {
                    setCopyModalVisible(false);
                    copyForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={copyForm}
                    layout="vertical"
                    onFinish={handleCopyPermissions}
                >
                    <Form.Item
                        name="fromRoleId"
                        label="Sao chép từ vai trò"
                        rules={[{ required: true, message: 'Vui lòng chọn vai trò nguồn' }]}
                    >
                        <Select placeholder="Chọn vai trò nguồn">
                            {availableRoles.map(role => (
                                <Option key={role.id} value={role.id}>
                                    {role.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="toRoleId"
                        label="Sao chép đến vai trò"
                        rules={[{ required: true, message: 'Vui lòng chọn vai trò đích' }]}
                    >
                        <Select placeholder="Chọn vai trò đích">
                            {availableRoles.map(role => (
                                <Option key={role.id} value={role.id}>
                                    {role.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button onClick={() => setCopyModalVisible(false)}>
                                Hủy
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Sao chép
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PermissionMatrix;